import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';

import yup from '../../utility/yup';
import {
  ItemForm,
  ViewFrame,
  FormInput,
  Permission,
} from '../../components';
import { useTitle } from '../../hooks';
import { is, roles } from '../../utility/guard';
import { Loadable } from '../../store/types';
import { Video } from '../../types/items';
import SavedSearchService, { SavedSearchPages } from '../../services/SavedSearchService';

const videoSchema = yup.object().shape({
  title: yup.string().required().trim(),
  file_size: yup.number().typeError('File Size must be a number'),
});

type Props = {
  creatingMode?: boolean;
  record: Loadable<Video>;
  onSubmit: (data: Video) => void;
};

const VideoForm = ({
  creatingMode = false,
  record: { loading, data },
  onSubmit,
}: Props) => {
  const dispatch = useDispatch();
  const title = data?.title || 'New Video';

  useTitle(title);

  useEffect(() => {
    const hotKeyList = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key?.toLocaleLowerCase() === 'l') {
        event.preventDefault();
        dispatch(push('/dashboard/admin/videos'));
      }
    };
    window.addEventListener('keydown', hotKeyList);

    return () => {
      window.removeEventListener('keydown', hotKeyList);
    };
  }, [dispatch]);

  return (
    <ViewFrame
      loading={loading}
      title={title}
      backToListLink
    >
      <ItemForm
        creatingMode={creatingMode}
        initialValues={data}
        validationSchema={videoSchema}
        onSubmit={onSubmit}
        loading={loading}
        editPermitted={Boolean(data?.permissions.can_edit)}
        enableReinitialize
        preventCancelChanges
        validateOnMount={false}
        validateOnBlur={false}
        validateOnChange
      >
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormInput
              label="Title"
              name="title"
              autoFocus
              getValueAfterInit={() => SavedSearchService.getValue(SavedSearchPages.Videos)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              label="Description"
              name="description"
              type="textarea"
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              label="Owner"
              name="ref.user.full_name_first_last"
              disabled
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              label="Organization"
              name="ref.organization.common_name"
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              label="Created"
              name="ref.created_age"
              disabled
            />
          </Grid>
          <Permission guard={is(roles.superAdmin)}>
            <Grid item sm={6} xs={12}>
              <FormInput
                label="File Size"
                name="file_size"
                disabled
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormInput
                label="Storage System"
                name="ref.storage_system.description"
                disabled
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormInput
                label="Storage Id"
                name="storage_unique_id"
                disabled
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormInput
                label="Delivery System"
                name="ref.delivery_system.description"
                disabled
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormInput
                label="Delivery Id"
                name="delivery_unique_id"
                disabled
              />
            </Grid>
          </Permission>
        </Grid>
      </ItemForm>
    </ViewFrame>
  );
};

export default VideoForm;
